import React, { Component, ChangeEvent } from 'react';
import './tab-config.css';
import { Form, InputProps, Button, Radio } from 'semantic-ui-react';
import { CONFIG } from '../../helper/config';
import history from '../../helper/history';
import * as microsoftTeams from "@microsoft/teams-js";
import { NARRATIVEBOARDCONFIG } from '../narrative-board/narrative-board-config';

interface IProps {

}

interface IState {
    selected_tab: string;
}

/**
 * **TabConfig Component**
 * 
 * A React component that allows users to configure and save their preferred 
 * Teams tab (e.g., Notifications or Narrative Board).  
 * 
 * This component integrates with the Microsoft Teams SDK to handle tab 
 * configuration, saving, and validation within a Teams app. It renders a 
 * simple UI with radio options for available tabs and a save button.
 * 
 * ### Features:
 * - Initializes the Teams SDK on mount
 * - Handles user input for tab selection
 * - Saves configuration to Microsoft Teams
 * - Provides feedback via `notifySuccess`
 */
export default class TabConfig extends Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = { selected_tab: '' };
    }

    /**
     * Lifecycle method called after the component mounts.  
     * Initializes the Microsoft Teams app and sends a success notification.
     */
    componentDidMount(){
        microsoftTeams.app.initialize();
        microsoftTeams.app.notifySuccess();
    }

    /**
     * Handles radio button selection change.
     * 
     * @param event - The React change event
     * @param data - The Semantic UI input props containing `name` and `value`
     */
    handleInputChange = (event: ChangeEvent, data: InputProps) => {
        const { value, name } = data;
        this.setState({ [name]: value } as Pick<IState, keyof IState>);
    }

    /**
     * Registers and saves the selected tab configuration in Microsoft Teams.
     * 
     * - Builds a tab URL based on the selected option.
     * - Registers the save handler.
     * - Calls `notifySuccess` after saving configuration.
     */
    saveGray = () => {
        let { selected_tab } = this.state;
        microsoftTeams.pages?.config.registerOnSaveHandler((saveEvent) => {
            let base_url = document.location.origin;
            let path = (selected_tab === CONFIG.TAB_CONFIG.TAB_CONFIG_NARRATIVE_BOARD_CHECKBOX_VALUE) ? CONFIG.PATH.NARRATIVEBOARD : CONFIG.PATH.NOTIFICATIONS;
            let url = base_url + path + '?' + history.location.search.substring(1);
            microsoftTeams.pages?.config.setConfig({
                websiteUrl: url,
                contentUrl: url,
                entityId: "beagletab",
                subEntityId: "sub_beagletab",
                suggestedDisplayName: selected_tab
            } as any);
            saveEvent.notifySuccess();
        });
    }

    /**
     * Sets the config validity to `true` and calls {@link saveGray}.
     * This method is triggered by clicking the **Save** button.
     */
    colorClickGray = () => {
        console.log("colorClickGray called...");
        microsoftTeams.pages?.config.setValidityState(true);
        this.saveGray();
    }

    /**
     * Renders the tab configuration UI.  
     * 
     * - Displays radio options for "Notifications" and "Narrative Board"
     * - Provides a Save button to persist configuration
     * 
     * @returns JSX.Element
     */
    render() {
        let { selected_tab } = this.state;
        return <div id='outer-container'>
            <div id='tab-config-container'>
                <p>{CONFIG.TAB_CONFIG.TAB_CONFIG_MSG}</p>
                <Form>
                <Form.Field
                    control={Radio}
                    label={CONFIG.TAB_CONFIG.TAB_CONFIG_NOTIFICATION_CHECKBOX_LABEL}
                    onChange={this.handleInputChange}
                    name='selected_tab'
                    value={CONFIG.TAB_CONFIG.TAB_CONFIG_NOTIFICATION_CHECKBOX_VALUE}
                    checked={selected_tab.includes(CONFIG.TAB_CONFIG.TAB_CONFIG_NOTIFICATION_CHECKBOX_VALUE)}
                />
                <Form.Field
                    control={Radio}
                    label={CONFIG.TAB_CONFIG.TAB_CONFIG_NARRATIVE_BOARD_CHECKBOX_LABEL}
                    onChange={this.handleInputChange}
                    name='selected_tab'
                    value={CONFIG.TAB_CONFIG.TAB_CONFIG_NARRATIVE_BOARD_CHECKBOX_VALUE}
                    checked={selected_tab.includes(CONFIG.TAB_CONFIG.TAB_CONFIG_NARRATIVE_BOARD_CHECKBOX_VALUE)}
                />
                </Form>
                <Button id='save-btn' size='mini' primary onClick={() => this.colorClickGray()} >{NARRATIVEBOARDCONFIG.BUTTONTEXT.SAVE}</Button>
            </div>
        </div>
    }
}